<template>
    <div class="language-switcher">
        <div class="relative">
            <button
                @click="toggleDropdown"
                class="lang-button flex items-center"
                :aria-expanded="isOpen"
                aria-haspopup="true"
            >
                <span>{{ currentLanguage.native_name }}</span>
                <span class="ml-1">{{ currentLanguage.flag }}</span>
                <svg class="ml-1 w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
            </button>

            <div
                v-if="isOpen"
                class="lang-dropdown"
                @click.stop
            >
                <a
                    v-for="(lang, code) in supportedLanguages"
                    :key="code"
                    :href="getLanguageUrl(code)"
                    :class="['lang-item', { 'lang-active': $page.props.locale === code }]"
                >
                    <span>{{ lang.native_name }}</span>
                    <span class="ml-2">{{ lang.flag }}</span>
                </a>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { usePage } from '@inertiajs/vue3';

// Get the current page object to access props
const $page = usePage();

// State for dropdown
const isOpen = ref(false);

// Toggle dropdown
const toggleDropdown = () => {
    isOpen.value = !isOpen.value;
};

// Close dropdown when clicking outside
const closeDropdown = (event) => {
    if (isOpen.value && !event.target.closest('.language-switcher')) {
        isOpen.value = false;
    }
};

// Add event listener for clicks outside
onMounted(() => {
    document.addEventListener('click', closeDropdown);
    // console.log(`Current locale: ${$page.props.locale}`);
    // console.log('Supported locales:', $page.props.supportedLocales);
});

// Remove event listener on component unmount
onBeforeUnmount(() => {
    document.removeEventListener('click', closeDropdown);
});

// Get supported languages from page props or use default
const supportedLanguages = computed(() => {
    return $page.props.supportedLocales || {
        'nl': { name: 'Nederlands', native_name: 'Nederlands', flag: '🇳🇱' },
        'en': { name: 'English', native_name: 'English', flag: '🇬🇧' },
        'de': { name: 'German', native_name: 'Deutsch', flag: '🇩🇪' },
        'fr': { name: 'French', native_name: 'Français', flag: '🇫🇷' },
        'pt': { name: 'Portuguese', native_name: 'Português', flag: '🇵🇹' },
        'es': { name: 'Spanish', native_name: 'Español', flag: '🇪🇸' },
        'zh': { name: 'Chinese', native_name: '中文', flag: '🇨🇳' }
    };
});

// Get current language
const currentLanguage = computed(() => {
    const locale = $page.props.locale || 'nl';
    return supportedLanguages.value[locale] || supportedLanguages.value['nl'];
});

// Get language URL from the languageSwitcherUrls passed by backend
const getLanguageUrl = (code) => {
    // Use the languageSwitcherUrls from the backend if available
    const languageSwitcherUrls = $page.props.languageSwitcherUrls || {};

    // If we have the URL for this language, use it
    if (languageSwitcherUrls[code]) {
        return languageSwitcherUrls[code];
    }

    // Fallback to the old approach if URL not available
    return `/switch-language/${code}`;
};
</script>

<style scoped>
.language-switcher {
    position: relative;
    display: flex;
    align-items: center;
}

.lang-button {
    padding: 0.25rem 0.5rem;
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
    border-radius: 0.25rem;
    transition: color 0.2s, background-color 0.2s;
    cursor: pointer;
    display: flex;
    align-items: center;
}

.lang-button:hover {
    color: #3b82f6;
    background-color: #f1f5f9;
}

.lang-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
    width: 10rem;
    background-color: white;
    border-radius: 0.375rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 50;
    overflow: hidden;
}

.lang-item {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    color: #64748b;
    font-size: 0.875rem;
    transition: background-color 0.2s;
    cursor: pointer;
}

.lang-item:hover {
    background-color: #f1f5f9;
    color: #3b82f6;
}

.lang-active {
    color: #3b82f6;
    font-weight: 600;
    background-color: #f1f5f9;
}

@media (prefers-color-scheme: dark) {
    .lang-button {
        color: #94a3b8;
    }

    .lang-button:hover {
        color: #3b82f6;
        background-color: #1e293b;
    }

    .lang-dropdown {
        background-color: #0f172a;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3);
    }

    .lang-item {
        color: #94a3b8;
    }

    .lang-item:hover {
        background-color: #1e293b;
        color: #3b82f6;
    }

    .lang-active {
        color: #3b82f6;
        background-color: #1e293b;
    }
}
</style>
